"use client";

import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Check, ShieldCheck, PhoneCall, Sparkles } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";

export function CTASection() {
  const t = useTranslations("HomePage.CTASection");

  // Magnetic Button Effect State
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [btnPos, setBtnPos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    setBtnPos({ x: x * 0.15, y: y * 0.15 });
  };

  const handleMouseLeave = () => {
    setBtnPos({ x: 0, y: 0 });
  };

  return (
    <section className="relative overflow-hidden border-b border-border px-6 py-20 md:px-12 md:py-32 bg-gradient-to-b from-background via-muted/30 to-background">
      <div className="mx-auto max-w-4xl">
        <div
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="relative overflow-hidden rounded-3xl border border-border/80 bg-gradient-to-br from-[#FF6B3D]/10 via-background to-[#4B3FD1]/10 p-8 sm:p-10 md:p-14 text-center shadow-2xl backdrop-blur-xl animate-gradient-loop"
        >
          {/* Background Ambient Blobs */}
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#FF6B3D]/15 blur-3xl animate-blob-float-1 pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-[#4B3FD1]/15 blur-3xl animate-blob-float-2 pointer-events-none" />

          <div className="relative z-10">
            <h2 className="mb-4 text-balance text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl md:text-5xl">
              {t("title")}
            </h2>

            <p className="mb-8 text-pretty text-base text-muted-foreground sm:text-lg max-w-2xl mx-auto leading-relaxed">
              {t("description")}
            </p>

            {/* Transparent SaaS Pricing Card */}
            <div className="my-8 mx-auto max-w-lg rounded-2xl border border-border/80 bg-card/90 p-6 shadow-xl backdrop-blur-md text-left">
              <div className="flex items-center justify-between border-b border-border/60 pb-4 mb-4">
                <div>
                  <h3 className="text-lg font-extrabold text-foreground">
                    Career Loop Premium Pass
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Full access to all 11+ AI Career Tools
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black text-[#FF6B3D]">2 ETB</div>
                  <div className="text-[11px] font-semibold text-muted-foreground">
                    per day
                  </div>
                </div>
              </div>

              {/* What's Included List */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-6 text-xs font-medium text-foreground">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-[#14B8A6] shrink-0" />
                  <span>3-Day Free Trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-[#14B8A6] shrink-0" />
                  <span>Unlimited Resume Builder</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-[#14B8A6] shrink-0" />
                  <span>AI Voice Mock Interview</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-[#14B8A6] shrink-0" />
                  <span>Job Matcher & Salary Coach</span>
                </div>
              </div>

              {/* Ethio Telecom Carrier Billing Explanation */}
              <div className="rounded-xl bg-muted/60 p-3.5 border border-border/60 text-xs text-muted-foreground space-y-1.5">
                <div className="flex items-center gap-1.5 font-bold text-foreground">
                  <ShieldCheck className="h-4 w-4 text-[#14B8A6]" />
                  <span>Transparent Ethio Telecom Carrier Billing</span>
                </div>
                <p className="leading-relaxed">
                  Send <strong className="text-foreground font-bold">"OK"</strong> to{" "}
                  <strong className="text-[#FF6B3D] font-bold">8526</strong> to activate your 3-day free trial. Cancel anytime instantly by sending{" "}
                  <strong className="text-foreground font-bold">"STOP"</strong> to{" "}
                  <strong className="text-[#FF6B3D] font-bold">8526</strong>. No credit card required.
                </p>
              </div>
            </div>

            {/* Breathing Glow Button with Magnetic Hover */}
            <div className="flex flex-col items-center gap-3">
              <Link href="/login">
                <Button
                  ref={buttonRef}
                  size="lg"
                  className="group relative h-13 px-9 text-base font-extrabold bg-[#FF6B3D] text-white hover:bg-[#FF6B3D]/90 animate-breathing-button transition-transform duration-150"
                  style={{
                    transform: `translate(${btnPos.x}px, ${btnPos.y}px)`,
                  }}
                >
                  <Sparkles className="mr-2 h-5 w-5" />
                  <span>{t("button")}</span>
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>

              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mt-2">
                <PhoneCall className="h-3.5 w-3.5 text-muted-foreground" />
                <span>Support Line: 0996330808</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
